// Snippet metadata is persisted inside the workbook via a Custom XML Part — the same
// mechanism the original VSTO add-in used (and which the Office.js runtime *does* expose,
// `workbook.customXmlParts`). It has no practical size ceiling (unlike Office settings),
// so a workbook with very many snippets keeps saving cleanly. Source-document binaries are
// stored in their own per-document parts (see saveDocSource). Legacy workbooks that still
// hold the old settings blob are migrated transparently on first load.
import type { PageComment, SnipDocument, Snippet } from '../types'

const KEY = 'snipcell.reforged.state.v1' // legacy Office-settings key (migration source only)
const META_NS = 'urn:snipcell:meta'

export interface PersistedState {
  documents: SnipDocument[]
  snippets: Snippet[]
  comments?: PageComment[]
}

function settings(): Office.Settings | null {
  try {
    return Office.context?.document?.settings ?? null
  } catch {
    return null
  }
}

async function readMetaPart(): Promise<string | null> {
  if (typeof Excel === 'undefined') return null
  return Excel.run(async (ctx) => {
    const parts = ctx.workbook.customXmlParts.getByNamespace(META_NS)
    parts.load('items')
    await ctx.sync()
    if (!parts.items.length) return null
    const xml = parts.items[0].getXml()
    await ctx.sync()
    return extractCdata(xml.value)
  })
}

async function writeMetaPart(json: string): Promise<void> {
  if (typeof Excel === 'undefined') throw new Error('Excel unavailable')
  await Excel.run(async (ctx) => {
    const existing = ctx.workbook.customXmlParts.getByNamespace(META_NS)
    existing.load('items')
    await ctx.sync()
    existing.items.forEach((p) => p.delete())
    ctx.workbook.customXmlParts.add(`<m xmlns="${META_NS}"><![CDATA[${json}]]></m>`)
    await ctx.sync()
  })
}

export async function loadState(): Promise<PersistedState> {
  const empty: PersistedState = { documents: [], snippets: [], comments: [] }
  let raw: string | null = null

  try {
    raw = await readMetaPart()
  } catch {
    raw = null
  }
  // Migration / fallback: pick up the legacy Office-settings blob if the XML part is absent.
  if (!raw) {
    const s = settings()
    raw = s ? ((s.get(KEY) as string | undefined) ?? null) : null
  }
  if (!raw) return empty

  try {
    const parsed = JSON.parse(raw) as PersistedState
    return {
      documents: Array.isArray(parsed.documents) ? parsed.documents : [],
      snippets: Array.isArray(parsed.snippets) ? parsed.snippets : [],
      comments: Array.isArray(parsed.comments) ? parsed.comments : [],
    }
  } catch {
    return empty
  }
}

export async function saveState(state: PersistedState): Promise<void> {
  const json = JSON.stringify(state)
  try {
    await writeMetaPart(json)
    return
  } catch {
    /* fall through to settings */
  }
  // Fallback when custom XML parts are unavailable (e.g. a non-Excel host).
  const s = settings()
  if (!s) return
  s.set(KEY, json)
  await new Promise<void>((resolve) => s.saveAsync(() => resolve()))
}

// ---- Embedded source bytes (so pages survive reopen without re-importing) ----
// Stored in the workbook's Custom XML Parts — the same mechanism the original VSTO add-in used,
// which holds far more than add-in settings. One part per document, keyed by a per-doc namespace;
// the source data URL is the CDATA content. Larger cap; only pathologically huge files fall back.
const MAX_SOURCE_BYTES = 25 * 1024 * 1024 // ~25 MB data-URL string

function srcNamespace(docId: string): string {
  return `urn:snipcell:src:${docId}`
}

function extractCdata(xml: string): string | null {
  const m = xml.match(/<!\[CDATA\[([\s\S]*?)\]\]>/)
  return m ? m[1] : null
}

export async function saveDocSource(docId: string, dataUrl: string): Promise<boolean> {
  if (typeof Excel === 'undefined' || dataUrl.length > MAX_SOURCE_BYTES) return false
  const ns = srcNamespace(docId)
  const xml = `<s xmlns="${ns}"><![CDATA[${dataUrl}]]></s>`
  try {
    await Excel.run(async (ctx) => {
      const existing = ctx.workbook.customXmlParts.getByNamespace(ns)
      existing.load('items')
      await ctx.sync()
      existing.items.forEach((p) => p.delete())
      ctx.workbook.customXmlParts.add(xml)
      await ctx.sync()
    })
    return true
  } catch {
    return false
  }
}

export async function loadDocSource(docId: string): Promise<string | null> {
  if (typeof Excel === 'undefined') return null
  const ns = srcNamespace(docId)
  try {
    return await Excel.run(async (ctx) => {
      const parts = ctx.workbook.customXmlParts.getByNamespace(ns)
      parts.load('items')
      await ctx.sync()
      if (!parts.items.length) return null
      const xml = parts.items[0].getXml()
      await ctx.sync()
      return extractCdata(xml.value)
    })
  } catch {
    return null
  }
}

export async function removeDocSource(docId: string): Promise<void> {
  if (typeof Excel === 'undefined') return
  const ns = srcNamespace(docId)
  try {
    await Excel.run(async (ctx) => {
      const parts = ctx.workbook.customXmlParts.getByNamespace(ns)
      parts.load('items')
      await ctx.sync()
      parts.items.forEach((p) => p.delete())
      await ctx.sync()
    })
  } catch {
    /* ignore */
  }
}
